<template>
  <div>
    <a-card :style="{ maxWidth: activeSettingsTab === 'notifications' ? '980px' : '640px' }">
      <a-tabs v-model:activeKey="activeSettingsTab">
        <a-tab-pane key="system" tab="系统设置">
          <a-spin :spinning="loading">
            <a-form layout="vertical" @finish="onSave">
              <a-form-item
                label="每个节点最多允许不同 IP 数"
                extra="同一账号最多允许多少个不同的玩家 IP 同时连接。同一 IP 上的连接数不限。范围 1-1000。"
              >
                <a-input-number
                  v-model:value="form.max_concurrent_clients"
                  :min="1"
                  :max="1000"
                  style="width:200px"
                />
                <span style="margin-left:8px;color:#6b7280">当前值</span>
              </a-form-item>

              <a-form-item label="说明">
                <p style="color:#6b7280;font-size:12px;margin:0">
                  修改后只会影响<strong>新创建</strong>的节点，已存在的节点不会改动。
                  同一个账号从超过这个数量的不同 IP 同时连接时，第 N+1 个 IP 会被服务器拒绝。
                  单一 IP 上的连接数（同一台电脑开多个游戏客户端）不受此限制。
                </p>
              </a-form-item>

              <a-button type="primary" html-type="submit" :loading="saving">保存</a-button>
            </a-form>
          </a-spin>
        </a-tab-pane>

        <a-tab-pane key="notifications" tab="通知设置">
          <NotificationSettingsPanel />
        </a-tab-pane>

        <a-tab-pane key="version" tab="版本信息">
          <a-spin :spinning="versionLoading">
            <a-descriptions :column="1" bordered size="small">
              <a-descriptions-item label="当前版本">
                <span style="font-family: ui-monospace, monospace">
                  {{ versionInfo?.version || 'unknown' }}
                </span>
                <span v-if="versionInfo?.git_sha" style="color:#6b7280;margin-left:8px">
                  ({{ versionInfo.git_sha }})
                </span>
              </a-descriptions-item>
              <a-descriptions-item label="更新时间">
                <span v-if="versionInfo?.built_at && versionInfo.built_at !== 'unknown'">
                  {{ formatBuiltAt(versionInfo.built_at) }}
                </span>
                <span v-else style="color:#6b7280">unknown</span>
              </a-descriptions-item>
              <a-descriptions-item label="后端语言">{{ versionInfo?.go_version || 'go' }}</a-descriptions-item>
              <a-descriptions-item label="前端">Vue 3 + Ant Design Vue 4.x</a-descriptions-item>
              <a-descriptions-item label="数据库">MySQL 8.x</a-descriptions-item>
              <a-descriptions-item label="Agent binary">
                <div v-if="versionInfo?.agent_version && versionInfo.agent_version !== 'unknown'" style="margin-bottom:6px">
                  <span style="color:#6b7280;font-size:12px">最新版本（install 时）</span>
                  <a-tag color="blue" style="margin-left:6px">
                    {{ versionInfo.agent_version }}
                  </a-tag>
                </div>
                <div v-if="agentVersions.length === 0" style="color:#9ca3af;font-size:12px">
                  暂无节点
                </div>
                <div v-else>
                  <div
                    v-for="n in agentVersions"
                    :key="n.id"
                    style="font-family: ui-monospace, monospace; font-size: 12px; line-height: 1.6"
                  >
                    <span style="color:#374151">{{ n.name }}</span>
                    <a-tag
                      v-if="n.agent_version && n.agent_version === latestAgentVersion && latestAgentVersion !== 'unknown'"
                      color="green"
                      style="margin-left:6px"
                    >{{ n.agent_version }}</a-tag>
                    <a-tag
                      v-else-if="n.agent_version"
                      color="orange"
                      style="margin-left:6px"
                      :title="`与最新版本 ${latestAgentVersion} 不一致，建议通过节点升级页面推送最新 binary`"
                    >{{ n.agent_version }}</a-tag>
                    <a-tag v-else color="default" style="margin-left:6px">unknown</a-tag>
                    <span style="color:#9ca3af;margin-left:4px">pid {{ n.child_count }}</span>
                  </div>
                  <div style="color:#6b7280;font-size:11px;margin-top:6px">
                    绿色 = 与 admin 内置 binary 版本一致；橙色 = 旧版本需要升级
                  </div>
                </div>
              </a-descriptions-item>
            </a-descriptions>
          </a-spin>
        </a-tab-pane>

        <a-tab-pane key="changelog" tab="更新日志">
          <a-tabs v-model:activeKey="activeChangelogTab" size="small">
            <a-tab-pane key="admin" tab="Admin 更新日志">
              <a-collapse size="small" ghost>
                <a-collapse-panel
                  v-for="item in adminChangelog"
                  :key="`admin-${item.version}-${item.date}`"
                  :header="`${item.version}（${item.date}）`"
                >
                  <ul style="margin:0;padding-left:18px">
                    <li v-for="(c, idx) in item.changes" :key="`admin-${item.version}-${idx}`" style="margin-bottom:4px">
                      {{ c }}
                    </li>
                  </ul>
                </a-collapse-panel>
              </a-collapse>
            </a-tab-pane>
            <a-tab-pane key="agent" tab="Agent 更新日志">
              <a-collapse size="small" ghost>
                <a-collapse-panel
                  v-for="item in agentChangelog"
                  :key="`agent-${item.version}-${item.date}`"
                  :header="`${item.version}（${item.date}）`"
                >
                  <ul style="margin:0;padding-left:18px">
                    <li v-for="(c, idx) in item.changes" :key="`agent-${item.version}-${idx}`" style="margin-bottom:4px">
                      {{ c }}
                    </li>
                  </ul>
                </a-collapse-panel>
              </a-collapse>
            </a-tab-pane>
          </a-tabs>
          <div style="color:#6b7280;font-size:12px;margin-top:8px">
            发布新版本时，更新 `admin/web/src/data/changelog.ts` 即可同步这里的展示内容。
          </div>
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import client from '../api/client';
import { getAdminVersion, type AdminVersion } from '../api/version';
import { adminChangelog, agentChangelog } from '../data/changelog';
import NotificationSettingsPanel from '../components/NotificationSettingsPanel.vue';

const route = useRoute();

const form = ref({ max_concurrent_clients: 3 });
const loading = ref(false);
const saving = ref(false);
const versionInfo = ref<AdminVersion | null>(null);
const versionLoading = ref(false);
const activeSettingsTab = ref<'system' | 'notifications' | 'version' | 'changelog'>('system');
const activeChangelogTab = ref<'admin' | 'agent'>('admin');

const load = async () => {
  loading.value = true;
  try {
    const r = await client.get('/api/settings');
    const data = r.data || {};
    form.value.max_concurrent_clients = data.max_concurrent_clients ?? 3;
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '加载失败');
  } finally {
    loading.value = false;
  }
};

const loadVersion = async () => {
  versionLoading.value = true;
  try {
    versionInfo.value = await getAdminVersion();
  } catch (e: any) {
    // Don't show a toast — version info is non-essential. Operator can
    // still navigate the rest of the page; the card will stay blank.
    // Common cause: 401 (cookie expired) triggers the client
    // interceptor's redirect anyway, so this path is mostly network
    // / parse errors.
    versionInfo.value = null;
  } finally {
    versionLoading.value = false;
  }
};

interface NodeVersion {
  id: number;
  name: string;
  agent_version: string;
  child_count: number;
}
const agentVersions = ref<NodeVersion[]>([]);

// Latest known agent version should prefer admin's configured upgrade target
// (shown as "Agent binary" above) and only fall back to node-reported
// versions when the admin sidecar is unavailable.
const latestAgentVersion = computed(() => {
  const adminTarget = versionInfo.value?.agent_version || 'unknown';
  if (adminTarget && adminTarget !== 'unknown') {
    return adminTarget;
  }
  const counts: Record<string, number> = {};
  for (const n of agentVersions.value) {
    const v = n.agent_version || 'unknown';
    counts[v] = (counts[v] || 0) + 1;
  }
  let best = 'unknown';
  let bestCount = 0;
  for (const [v, c] of Object.entries(counts)) {
    if (v !== 'unknown' && c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return best;
});

const loadNodes = async () => {
  try {
    const r = await client.get('/api/nodes');
    const list = Array.isArray(r.data) ? r.data : [];
    agentVersions.value = list
      .filter((n: any) => !n.deleted_at)
      .map((n: any) => ({
        id: n.id,
        name: n.name,
        agent_version: n.agent_version || '',
        child_count: n.used_ports ?? 0,
      }));
  } catch {
    agentVersions.value = [];
  }
};

// Format a UTC RFC3339 timestamp into Asia/Shanghai local time, e.g.
//   2026-07-04T15:30:00Z  →  2026-07-04 23:30:00 (CST)
// Falls back to the input string on parse error.
const formatBuiltAt = (s: string): string => {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // Intl with the IANA zone name; older browsers may need a polyfill,
  // but Ant Design Vue 4 already requires a modern Chromium / WebKit,
  // so this is safe.
  return d.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const onSave = async () => {
  saving.value = true;
  try {
    await client.put('/api/settings', {
      key: 'max_concurrent_clients',
      value: form.value.max_concurrent_clients,
    });
    message.success('已保存');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '保存失败');
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  if (route.query.tab === 'notifications') activeSettingsTab.value = 'notifications';
  load();
  loadVersion();
  loadNodes();
});
</script>
